import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Stack,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext, useVenuesApi } from "@/context";
import { Breadcrumbs } from "@/utils/components/Breadcrumbs";

interface VenueFormData {
  name: string;
  venueType: string;
  description: string;
  capacity: number | "";
  address: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  contactInfo: {
    phone?: string;
    email?: string;
    website?: string;
  };
  technicalSpecs: {
    soundSystem?: string;
    lighting?: string;
    stageDimensions?: string;
    loadInNotes?: string;
  };
  isActive: boolean;
}

const VENUE_TYPE_CHOICES = [
  { value: "club", label: "Club" },
  { value: "theater", label: "Theater" },
  { value: "arena", label: "Arena" },
  { value: "festival", label: "Festival" },
  { value: "outdoor", label: "Outdoor" },
  { value: "other", label: "Other" },
];

const initialFormData: VenueFormData = {
  name: "",
  venueType: "",
  description: "",
  capacity: "",
  address: {},
  contactInfo: {},
  technicalSpecs: {},
  isActive: true,
};

export const VenueForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { team } = useAppContext();
  const venuesApi = useVenuesApi();
  const [formData, setFormData] = useState<VenueFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingLimit, setCheckingLimit] = useState(true);

  const isEditMode = Boolean(id);

  useEffect(() => {
    checkVenueLimit();
  }, []);

  useEffect(() => {
    if (isEditMode) {
      fetchVenue();
    }
  }, [id]);

  const checkVenueLimit = async () => {
    // Only check limit when creating a new venue
    if (isEditMode) {
      setCheckingLimit(false);
      return;
    }

    try {
      const data = await venuesApi.venuesList();
      const venues = Array.isArray(data) ? data : data.results || [];

      // If team already has a venue, redirect back to list
      if (venues.length >= 1) {
        navigate("/venues", {
          replace: true,
          state: {
            message:
              "You already have a venue. Contact Solid Slate to add additional venues to your team.",
          },
        });
        return;
      }
    } catch {
      setError("Failed to check venue limit.");
    } finally {
      setCheckingLimit(false);
    }
  };

  const fetchVenue = async () => {
    try {
      setLoading(true);
      const venue = await venuesApi.venuesRetrieve({ id: id! });

      setFormData({
        name: venue.name || "",
        venueType: venue.venueType || "",
        description: venue.description || "",
        capacity: "", // Capacity not in Venue type
        address: venue.address || {},
        contactInfo: venue.contactInfo || {},
        technicalSpecs: venue.technicalSpecs || {},
        isActive: venue.isActive !== false,
      });
    } catch {
      setError("Failed to load venue. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!team?.id) {
      setError("Team context is required to create a venue.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const payload = {
        name: formData.name,
        venue_type: formData.venueType,
        description: formData.description,
        capacity: formData.capacity === "" ? null : Number(formData.capacity),
        address: formData.address,
        contact_info: formData.contactInfo,
        technical_specs: formData.technicalSpecs,
        is_active: formData.isActive,
        team: team.id,
      };

      if (isEditMode) {
        await venuesApi.venuesPartialUpdate({
          id: id!,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          patchedVenue: payload as any, // OpenAPI schema accepts snake_case
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await venuesApi.venuesCreate({ venue: payload as any }); // OpenAPI schema accepts snake_case
      }

      navigate("/venues");
    } catch {
      setError("Failed to save venue. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading venue...</Typography>
      </Stack>
    );
  }

  if (checkingLimit) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Checking venue limit...</Typography>
      </Stack>
    );
  }

  return (
    <Box>
      <Breadcrumbs
        items={
          isEditMode
            ? [
                { label: "Venues", path: "/venues" },
                { label: formData.name || "Loading...", path: `/venues/${id}` },
                { label: "Edit" },
              ]
            : [{ label: "Venues", path: "/venues" }, { label: "New Venue" }]
        }
      />
      <Typography variant="h4" gutterBottom>
        {isEditMode ? "Edit Venue" : "New Venue"}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}

              {/* Basic Information */}
              <Typography variant="h6">Basic Information</Typography>

              <TextField
                label="Venue Name"
                required
                fullWidth
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Venue Type"
                  select
                  required
                  fullWidth
                  value={formData.venueType}
                  onChange={(e) =>
                    setFormData({ ...formData, venueType: e.target.value })
                  }
                >
                  {VENUE_TYPE_CHOICES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Capacity"
                  type="number"
                  fullWidth
                  value={formData.capacity}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      capacity:
                        e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                  inputProps={{ min: 0 }}
                />
              </Stack>

              <TextField
                label="Description"
                multiline
                rows={3}
                fullWidth
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />

              <Divider />

              {/* Address */}
              <Typography variant="h6">Address</Typography>

              <TextField
                label="Street Address"
                fullWidth
                value={formData.address.street || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value },
                  })
                }
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="City"
                  fullWidth
                  value={formData.address.city || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value },
                    })
                  }
                />
                <TextField
                  label="State/Province"
                  fullWidth
                  value={formData.address.state || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, state: e.target.value },
                    })
                  }
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Postal Code"
                  fullWidth
                  value={formData.address.postalCode || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        postalCode: e.target.value,
                      },
                    })
                  }
                />
                <TextField
                  label="Country"
                  fullWidth
                  value={formData.address.country || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: { ...formData.address, country: e.target.value },
                    })
                  }
                />
              </Stack>

              <Divider />

              {/* Contact Information */}
              <Typography variant="h6">Contact Information</Typography>

              <Stack spacing={2}>
                <TextField
                  label="Phone"
                  fullWidth
                  value={formData.contactInfo.phone || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contactInfo: {
                        ...formData.contactInfo,
                        phone: e.target.value,
                      },
                    })
                  }
                />
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  value={formData.contactInfo.email || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contactInfo: {
                        ...formData.contactInfo,
                        email: e.target.value,
                      },
                    })
                  }
                />
                <TextField
                  label="Website"
                  fullWidth
                  value={formData.contactInfo.website || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contactInfo: {
                        ...formData.contactInfo,
                        website: e.target.value,
                      },
                    })
                  }
                  placeholder="https://..."
                />
              </Stack>

              <Divider />

              {/* Technical Specifications */}
              <Typography variant="h6">Technical Specifications</Typography>

              <Stack spacing={2}>
                <TextField
                  label="Sound System"
                  multiline
                  rows={2}
                  fullWidth
                  value={formData.technicalSpecs.soundSystem || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      technicalSpecs: {
                        ...formData.technicalSpecs,
                        soundSystem: e.target.value,
                      },
                    })
                  }
                  helperText="PA system, speakers, mixing console details"
                />
                <TextField
                  label="Lighting"
                  multiline
                  rows={2}
                  fullWidth
                  value={formData.technicalSpecs.lighting || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      technicalSpecs: {
                        ...formData.technicalSpecs,
                        lighting: e.target.value,
                      },
                    })
                  }
                  helperText="Available lighting equipment and capabilities"
                />
                <TextField
                  label="Stage Dimensions"
                  fullWidth
                  value={formData.technicalSpecs.stageDimensions || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      technicalSpecs: {
                        ...formData.technicalSpecs,
                        stageDimensions: e.target.value,
                      },
                    })
                  }
                  placeholder="e.g., 40' x 30' x 20' (W x D x H)"
                />
                <TextField
                  label="Load-In Notes"
                  multiline
                  rows={2}
                  fullWidth
                  value={formData.technicalSpecs.loadInNotes || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      technicalSpecs: {
                        ...formData.technicalSpecs,
                        loadInNotes: e.target.value,
                      },
                    })
                  }
                  helperText="Access information, dock details, restrictions"
                />
              </Stack>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                }
                label="Active (visible to team members)"
              />

              {/* Actions */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => navigate("/venues")}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={loading}>
                  {loading ? "Saving..." : isEditMode ? "Update" : "Create"}
                </Button>
              </Stack>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
